// Shared CONTEXT + OFFERABILITY rules for the sell-side Data Entry doors (Add Discount, Add Fees, Make Payment),
// so the three can never drift. Pure: no React, no network, no money arithmetic.
//
// Context (owner 2026-10-06), from the drawer params SalesDataEntryMenu dispatches:
//   inside an order    -> 'order'    : locked to that order (no picker)
//   inside a customer  -> 'customer' : picker prefiltered to that customer, customer shown as a locked chip
//   anywhere else      -> 'global'   : picker over every customer's orders
// Offerable = NOT DONE: order_lifecycle 'open' (API: 'closed' = no open line and goods/LC settled, server.js:24657;
// planned/quote are their own lifecycles), posted, not quote/cancelled. Done orders are handled from the order page.

export const PAGE_SIZE = 200;   // the doors' existing list size (Add Fees / Make Payment); ponytail: newest 200 only
// Owner rule for Add Fees: open orders only. The API itself ALLOWS a fee on a done order (POST /api/sales-fees has no
// lifecycle guard) — flip this to false to offer done orders to Add Fees again (open question in RESULT).
export const FEES_OPEN_ORDERS_ONLY = true;

export function resolveContext({ operationId = '', partnerId = '', partnerName = '' } = {}) {
  const lockedOrder = operationId || '';
  const lockedPartner = lockedOrder ? '' : (partnerId || '');
  const mode = lockedOrder ? 'order' : lockedPartner ? 'customer' : 'global';
  return { mode, lockedOrder, lockedPartner, partnerName: lockedPartner ? (partnerName || '') : '',
    listParams: { page_size: PAGE_SIZE, order_lifecycle: 'open', ...(lockedPartner ? { partner_id: lockedPartner } : {}) } };
}

export const isOfferable = (o) => !!o && !!o.operation_id
  && (o.tx_status == null || o.tx_status === 'posted')
  && String(o.order_lifecycle || '').toLowerCase() === 'open'
  && !['quote', 'cancelled'].includes(String(o.delivery_status || '').toLowerCase());

// List rows -> unique offerable orders, newest first (op_date desc, then operation_id desc, like the list endpoint).
export function offerableOrders(rows) {
  const seen = new Map();
  for (const r of rows || []) if (isOfferable(r) && !seen.has(r.operation_id)) seen.set(r.operation_id, r);
  const key = (o) => `${o.op_date || ''}|${o.operation_id}`;
  return [...seen.values()].sort((a, b) => key(b).localeCompare(key(a)));
}

// A typed operation id (OP-YYYYMMDD-HHmmss, optional suffix). Returns the trimmed, upper-cased id or ''.
const OP_ID = /^OP-\d{8}-\d{6}[A-Z0-9-]*$/i;
export const typedOpId = (q) => { const s = String(q || '').trim(); return OP_ID.test(s) ? s.toUpperCase() : ''; };

export const orderPageHref = (op) => `/sales/orders/${encodeURIComponent(op)}`;
export const DONE_ORDERS_HREF = '/sales/orders?lifecycle=closed';

// Row model for a typed id that is NOT among the offered options. Never an action: at most a link to the order page.
//   lookup: undefined (not asked yet / in flight) | { notFound: true } | { header } from GET /api/sales-orders/:op
//   -> null (nothing to show) | { kind: 'checking'|'notFound'|'done'|'elsewhere', op, href? }
export function typedOrderRow({ query, offeredIds = [], lookup }) {
  const op = typedOpId(query);
  if (!op || offeredIds.some((id) => String(id).toUpperCase() === op)) return null;
  if (lookup === undefined) return { kind: 'checking', op };
  if (!lookup || lookup.notFound || !lookup.header) return { kind: 'notFound', op };
  const real = lookup.header.operation_id || op;
  const done = String(lookup.header.order_lifecycle || '').toLowerCase() === 'closed';
  return { kind: done ? 'done' : 'elsewhere', op: real, href: orderPageHref(real) };
}
